'use client';
import { Box, Theme } from '@mui/material';
import { motion } from 'motion/react';
import { CardsType } from '../type';
import TextCard from '../grid/TextCard';
import DarkThemeProvider from '../../../../providers/DarkThemeProvider';
import LightThemeProvider from '../../../../providers/LightThemeProvider';
import useIsMobile from '../../../../utils/useIsMobile';
import CardRotate from './CardRotate';
import StackArrows from './StackArrows';
import { useCardStack } from './useCardStack';

type Props = {
  data: CardsType;
  isMobile: boolean;
};

const ANIMATION_CONFIG = { stiffness: 260, damping: 20 };
const DRAG_SENSITIVITY = 180;

const CardStack = ({ data, isMobile: initIsMobile }: Props) => {
  const { cards, styling, orientation = 'vertical', role, showArrowsOn } = data;
  const isMobile = useIsMobile(initIsMobile);
  const ThemeProvider = styling.isDark ? DarkThemeProvider : LightThemeProvider;

  const { stack, sendToBack, next, prev, setIsPaused } = useCardStack({
    cards,
  });

  const showArrows =
    showArrowsOn === 'both' ||
    (showArrowsOn === 'mobile' && isMobile) ||
    (showArrowsOn === 'desktop' && !isMobile) ||
    (!showArrowsOn && !isMobile);

  const step = stack.length > 3 ? 10 : 16;

  return (
    <ThemeProvider>
      <Box
        sx={{
          position: 'relative',
          maxWidth: 'sm',
          alignSelf: 'center',
          width: '100%',
        }}
      >
        {showArrows && <StackArrows onPrev={prev} onNext={next} />}
        <Box
          className="tb-cards-inner-container"
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: '450px',
            perspective: '600px',
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {stack.map((entry, index) => (
            <CardRotate
              key={entry.id}
              onSendToBack={() => sendToBack(entry.id)}
              sensitivity={DRAG_SENSITIVITY}
            >
              <motion.div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 'inherit',
                }}
                onClick={() => sendToBack(entry.id)}
                animate={{
                  transform: `translate(-${index * step * 0.4}px, ${index * step}px)`,
                  scale: 1 + index * 0.06 - stack.length * 0.06,
                }}
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: ANIMATION_CONFIG.stiffness,
                  damping: ANIMATION_CONFIG.damping,
                }}
              >
                <TextCard
                  orientation={orientation}
                  variant="static"
                  isMobile={isMobile}
                  moveAuthorTop={styling.listContainer.moveAuthorTop}
                  moveTagsTop={styling.listContainer.moveTagsTop}
                  {...entry.content}
                  role={role}
                  // @ts-ignore
                  sx={{
                    boxShadow: (theme: Theme) => theme.shadows[1],
                    ...styling.sx,
                  }}
                  imageSx={styling.imageSx}
                />
              </motion.div>
            </CardRotate>
          ))}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default CardStack;
